import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData, isNumeric } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global, globalMenu } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import { showPopupModal, showResizablePopupModal } from "@widgets/CommonModal";
import ExcelUpload from "@widgets/commonpopup/ExcelUpload";
import SN_PUR_BAS_REG_003_POP_01 from "./SN_PUR_BAS_REG_003_POP_01";
import SnButton from "@shared/components/SnButton";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import SnResizeBar from "@shared/components/SnResizeBar";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import * as XLSX from "xlsx";
interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

// 엑셀 업로드 시 기존 데이터 여부를 판정할 PK 조합 (mas_umcust)
const UPLOAD_PK_FIELDS = [
  "cd_firm",
  "cd_module",
  "cd_cust",
  "cd_item",
  "tp_um",
  "cd_exch",
  "dt_start",
];

// 엑셀 업로드 오류체크(필수값) 대상 컬럼
// 거래처/품목 중 하나는 좌측 목록에서 선택한 값으로 채우므로 탭별로 다르다.
const UPLOAD_REQUIRED_COMMON = [
  "cd_exch",
  "tp_um",
  "um",
  "um_supply",
  "dt_start",
];
// 업로드 양식은 탭과 무관하게 동일하므로 필수값도 하나로 쓴다.
const UPLOAD_REQUIRED_FIELDS = [
  "cd_item",
  "cd_cust",
  ...UPLOAD_REQUIRED_COMMON,
];

// 엑셀 업로드 양식 컬럼 (탭과 무관하게 이 순서로 고정)
const UPLOAD_FORM_COLUMNS: {
  cd_column: string;
  nm_column: string;
  qt_width?: number; // 지정하면 업로드 양식에서 이 너비로 고정
}[] = [
  { cd_column: "cd_item", nm_column: "품목코드" },
  { cd_column: "nm_item", nm_column: "품목명" },
  { cd_column: "cd_cust", nm_column: "거래처", qt_width: 160 },
  { cd_column: "nm_cust", nm_column: "거래처명" },
  { cd_column: "dc_item_spec", nm_column: "규격" },
  { cd_column: "dc_item_unit", nm_column: "단위" },
  { cd_column: "cd_exch", nm_column: "환종" },
  { cd_column: "tp_um", nm_column: "단가유형" },
  { cd_column: "um", nm_column: "단가" },
  { cd_column: "um_supply", nm_column: "부가세포함단가" },
  { cd_column: "dt_start", nm_column: "시작일" },
  { cd_column: "dt_end", nm_column: "종료일" },
  { cd_column: "dc_rmk", nm_column: "비고" },
  { cd_column: "um_mgmt1", nm_column: "관리단가1" },
  { cd_column: "um_mgmt2", nm_column: "관리단가2" },
  { cd_column: "um_mgmt3", nm_column: "관리단가3" },
  { cd_column: "um_mgmt4", nm_column: "관리단가4" },
  { cd_column: "um_mgmt5", nm_column: "관리단가5" },
];
// 종료일 기본값 (행추가 / 엑셀 업로드에서 종료일을 비워둔 경우)
const DEFAULT_DT_END = "21001231";
const today = new Date();
const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
  .toString()
  .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
const SN_PUR_BAS_REG_003 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
    grid4: React.createRef(),
  });
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [selectedTab, setSelectedTab] = useState<string>("1");
  const [initFlag, setInitFlag] = useState<boolean>(false);
  //#endregion

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_PUR_BAS_REG_003";
  const defaultSize = {
    panel1width: "49.5%",
    panel2width: "49.5%",
    panel3width: "49.5%",
    panel4width: "49.5%",
  };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize); // SnResizable들의 크기
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
        });

        setFormData((prevData: any) => ({
          ...prevData,
          cd_module: Menuprops.cd_module,
        }));
        setInitFlag(true);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (initFlag) onSearch();
  }, [selectedTab]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
    onExcelDownLoad,
    onExcelUpload,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid2"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid4"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
      }
    }
    try {
      const params = {
        id:
          selectedTab === "1"
            ? "sn_pur_bas_reg_003_01"
            : "sn_pur_bas_reg_003_02",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
      gridRefs.current["grid4"].current.resetUpdate();

      const data: any = await fnGetDataSet(params);

      if (selectedTab === "1") {
        setDataSources({
          grid1: data[0] || [],
          grid2: data[1] || [],
          grid3: [],
          grid4: [],
        });
      } else if (selectedTab === "2") {
        setDataSources({
          grid1: [],
          grid2: [],
          grid3: data[0] || [],
          grid4: data[1] || [],
        });
      }
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onAddRow = () => {
    try {
      if (selectedTab === "1") {
        const requiredValuesCheckH =
          gridRefs.current["grid1"].current.requiredValuesCheck();
        if (!requiredValuesCheckH) {
          return false;
        }
        return gridRefs.current["grid2"].current?.addRow({});
      } else if (selectedTab === "2") {
        return gridRefs.current["grid4"].current?.addRow({});
      }
    } catch (error: any) {}
  };
  //#region onBeforeAddRowL
  const onBeforeAddRowL = () => {
    try {
      //gridRefs.current["grid1"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const selectedRows = gridRefs.current["grid1"].current.focusedRowNode();
      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        tp_um: selectedFormData.tp_um,
        cd_exch: selectedFormData.cd_exch,
      };

      let Tpum = selectedData.tp_um;
      let CdExch = selectedData.cd_exch;
      return {
        cd_firm: userInfo.cd_firm,
        cd_module: "PUR",
        cd_cust: selectedRows.data?.cd_cust,
        tp_um: Tpum ? Tpum : "top1",
        dt_start: todayform,
        dt_end: DEFAULT_DT_END,
        cd_exch: CdExch ? CdExch : "top1",
        um: 0,
        um_supply: 0,
        cd_user_reg: userInfo.cd_user,
      };
    } catch (error: any) {
      return false;
    }
  };
  //#region onBeforeAddRow2
  const onBeforeAddRow2 = () => {
    try {
      gridRefs.current["grid3"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid3"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const selectedRows = gridRefs.current["grid3"].current.focusedRowNode();
      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        tp_um: selectedFormData.tp_um,
        cd_exch: selectedFormData.cd_exch,
      };
      let Tpum = selectedData.tp_um;
      let CdExch = selectedData.cd_exch;
      return {
        cd_firm: userInfo.cd_firm,
        cd_module: "PUR",
        cd_item: selectedRows.data?.cd_item,
        tp_um: Tpum ? Tpum : "top1",
        cd_exch: CdExch ? CdExch : "top1",
        dt_start: todayform,
        dt_end: DEFAULT_DT_END,
        um: 0,
        um_supply: 0,
        qt_dummy01: 0,
        um_before: 0,
        um_mgmt1: 0,
        um_mgmt2: 0,
        um_mgmt3: 0,
        um_mgmt4: 0,
        um_mgmt5: 0,
        cd_user_reg: userInfo.cd_user,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDeleteRow = async () => {
    if (selectedTab === "1") {
      if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;
      gridRefs.current["grid2"].current?.deleteSelectedRows();
    } else if (selectedTab === "2") {
      if (gridRefs.current["grid4"].current?.getDisplayedRowCount() < 1) return;
      gridRefs.current["grid4"].current?.deleteSelectedRows();
    }
  };
  //#region onSave
  const onSave = async () => {
    const checkDatesValid = (rows: any[]): boolean => {
      for (const row of rows) {
        const isValid = dateOk(row.dt_start, row.dt_end);
        if (!isValid) {
          return false;
        }
      }
      return true;
    };

    if (selectedTab === "1") {
      try {
        gridRefs.current["grid1"].current?.stopEditing();
        gridRefs.current["grid2"].current?.stopEditing();
        const requiredValuesCheckL =
          gridRefs.current["grid2"].current.requiredValuesCheck();
        if (!requiredValuesCheckL) {
          return;
        }
        const masumcustChanges = gridRefs.current["grid2"].current.getChange();
        const { insert = [], update = [] } = masumcustChanges;

        if (masumcustChanges.modifiedRowcount === 0) {
          await showDialog("alter", "변경된 내용이 없습니다.");
          return;
        }

        const isValid = checkDatesValid([...insert, ...update]);
        if (!isValid) {
          return;
        }

        const SaveData = {
          mas_umcust: masumcustChanges,
        };
        // 저장 API 호출
        await fnSave("SN_PUR_BAS_REG_003_SAVE_01", SaveData);
        // 성공적으로 저장된 후 초기화
        gridRefs.current["grid2"].current.resetUpdate();
      } catch (error: any) {
        showDialog("error", error.message);
      }
    } else {
      try {
        const requiredValuesCheck4 =
          gridRefs.current["grid4"].current.requiredValuesCheck();
        if (!requiredValuesCheck4) {
          return;
        }
        const masumcustChanges = gridRefs.current["grid4"].current.getChange();
        const { insert = [], update = [] } = masumcustChanges;

        if (masumcustChanges.modifiedRowcount === 0) {
          await showDialog("alter", "변경된 내용이 없습니다.");
          return;
        }

        const isValid = checkDatesValid([...insert, ...update]);
        if (!isValid) {
          return;
        }

        const SaveData = {
          mas_umcust: masumcustChanges,
        };
        await fnSave("SN_PUR_BAS_REG_003_SAVE_01", SaveData);

        gridRefs.current["grid4"].current.resetUpdate();
      } catch (error: any) {}
    }
  };
  //#endregion

  //#region Excel
  // 탭에 따라 단가 그리드가 다르다 (거래처별: grid2 / 품목별: grid4)
  const getUmGridRef = () =>
    selectedTab === "1"
      ? gridRefs.current["grid2"].current
      : gridRefs.current["grid4"].current;

  const getUmColumns = () =>
    selectedTab === "1" ? columns.grid2 : columns.grid4;

  /**
   * 엑셀 업로드 양식 컬럼.
   * 탭(거래처별/품목별)과 상관없이 UPLOAD_FORM_COLUMNS 순서 그대로 만든다.
   * 컬럼 정의는 두 단가 그리드(거래처별/품목별) 설정에서 찾아 쓰고,
   * 양쪽에 다 없으면 텍스트 컬럼으로 만들어 양식에서 빠지지 않게 한다.
   */
  const getUploadColumns = () => {
    const allColumns: any[] = [
      ...(columns.grid2 ?? []),
      ...(columns.grid4 ?? []),
    ];

    return UPLOAD_FORM_COLUMNS.map((form, index) => {
      const found = allColumns.find(
        (col: any) => col.cd_column === form.cd_column
      );

      if (found) {
        // 그리드 설정에 컬럼명이 비어 있으면 헤더에 필드코드가 그대로 찍히므로
        // 양식에 정의한 이름을 항상 쓴다.
        return {
          ...found,
          nm_column: form.nm_column,
          fg_visible: "Y",
          no_seq: index,
          ...(form.qt_width ? { qt_width: form.qt_width } : {}),
        };
      }

      return {
        cd_column: form.cd_column,
        nm_column: form.nm_column,
        tp_column: "SnTextBox",
        fg_visible: "Y",
        yn_required: "N",
        dc_editcon: "true",
        qt_width: form.qt_width ?? 120,
        no_seq: index,
      };
    });
  };

  /**
   * 엑셀 셀 값 변환.
   * 날짜(dt_*)는 화면 표기(2026/09/23)가 아니라 업로드 양식과 같은 yyyyMMdd 로,
   * 숫자는 숫자로, 나머지는 문자열로 넣는다.
   */
  const toExcelCellValue = (colId: string, value: any): string | number => {
    if (value == null) return "";

    const raw =
      typeof value === "object"
        ? value.label ?? value.text ?? value.value ?? ""
        : value;
    const text = raw?.toString() ?? "";

    if (colId.startsWith("dt_")) return text.replace(/[^0-9]/g, "");
    // 앞자리가 0 인 코드값은 숫자로 바꾸면 0 이 날아가므로 문자열로 둔다.
    if (text !== "" && !/^0\d/.test(text) && isNumeric(text)) {
      return Number(text);
    }

    return text;
  };

  /**
   * 엑셀 다운로드.
   * 엑셀 업로드 양식(UPLOAD_FORM_COLUMNS)과 컬럼 구성·순서를 똑같이 맞춰 내려준다.
   * (업로드는 붙여넣기 기준이라 컬럼 순서가 다르면 값이 밀린다)
   * forEachNode 는 필터와 무관하게 전체 행을 주므로, 좌측 목록에서 선택한
   * 거래처/품목 건만이 아니라 조회된 전체가 대상이 된다.
   */
  const onExcelDownLoad = async () => {
    const grid = getUmGridRef();
    if (!grid) return;

    try {
      const rows: any[] = [];
      grid.forEachNode((node: any) => {
        if (node.data) rows.push(node.data);
      });

      const sheetData = [
        UPLOAD_FORM_COLUMNS.map((col) => col.nm_column),
        ...rows.map((row) =>
          UPLOAD_FORM_COLUMNS.map((col) =>
            toExcelCellValue(col.cd_column, row[col.cd_column])
          )
        ),
      ];

      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        book,
        XLSX.utils.aoa_to_sheet(sheetData),
        "Sheet1"
      );
      XLSX.writeFile(
        book,
        `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`
      );
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onExcelUpload = async () => {
    try {
      // 업로드 양식은 탭과 무관하게 동일하다.
      // 거래처·품목 모두 엑셀에서 받으므로 좌측 목록 선택값은 쓰지 않는다.
      const component = (
        <ExcelUpload
          Menuprops={Menuprops}
          PkFields={UPLOAD_PK_FIELDS}
          tableName="mas_umcust" // SN_PUR_BAS_REG_003_SAVE_01 이 받는 데이터셋 키
          controllerName="SN_PUR_BAS_REG_003_SAVE_01"
          pkCheckCode="mas_umcust" // PK 중복 판정용 조회 코드
          defaultValue={{
            cd_firm: userInfo.cd_firm,
            cd_module: "PUR",
            cd_user_reg: userInfo.cd_user,
            // 종료일을 비워두고 올리면 행추가와 같은 기본값이 들어간다.
            dt_end: DEFAULT_DT_END,
          }}
          columnConfig={getUploadColumns()}
          requiredFields={UPLOAD_REQUIRED_FIELDS}
        />
      );

      const result = await showPopupModal({
        title: "엑셀 업로드",
        children: component,
        width: "80%",
        height: "90VH",
        okayBtnText: "저장",
      });

      if (result?.excelUpload) {
        await onSearch();
      }
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  //#region  Btn Event
  const onGrid2BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }

    if (id == "aButton_PopUp") {
      if (selectedTab === "2") {
        await showDialog("alter", "거래처별 탭에서만 사용 가능합니다.");
        return;
      }
      const selectedFormData = searchRef.current.getFormdata();
      const selectedRows = gridRefs.current["grid1"]?.current?.focusedRowNode();
      const selectedData = {
        tp_um: selectedFormData.tp_um,
        cd_cust: selectedRows?.data?.cd_cust || "",
        cd_cust_rcv: selectedRows?.data?.cd_cust || "",
        nm_cust: selectedRows?.data?.nm_cust || "",
        nm_cust_sort: selectedRows?.data?.nm_cust_sort || "",
      };

      const component = (
        <SN_PUR_BAS_REG_003_POP_01 selectedData={selectedData} />
      );
      const result = await showPopupModal({
        title: "신규품목적용",
        children: component,
        width: "65%",
        height: "60%",
        validation: true,
      });
      if (!result || result === "close" || result.length === 0) return;

      const headerResult = result[0];
      headerResult.map((result: any) => {
        const addedNode = onAddRow();
        if (addedNode) {
          for (const key in result) {
            //addedNode.setDataValue(key, result[key]);
            let value = result[key];

            // tp_um 컬럼이 비어있으면 form에서 가져오기
            if (
              key === "tp_um" &&
              (value === null || value === undefined || value === "")
            ) {
              const selectedFormData = searchRef.current.getFormdata();
              value = selectedFormData.tp_um;
            }

            addedNode.setDataValue(key, value);
          }
        }
      });
    }
  };

  const onGrid4BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  //#region  FocusedRowChanged

  //#region Method
  const dateOk = (startDate: string, endDate?: string): boolean => {
    const formatToNumber = (date: string) =>
      parseInt(date?.replace(/-/g, "") || "0", 10);

    const dtStart = formatToNumber(startDate);
    const dtEnd = formatToNumber(endDate || "21001231");

    if (dtStart > dtEnd) {
      // showDialog("alter", "종료일이 시작일보다 빠릅니다.");
      return false;
    }

    return true;
  };

  const onCellValueChanged = (params: any) => {
    let focusedRowNode;
    const { colDef, rowData, colId } = params;

    const updatedRow = params.data;
    const gridName = selectedTab === "1" ? "gridView2" : "gridView4";
    try {
      switch (gridName) {
        case "gridView2":
          focusedRowNode = gridRefs.current["grid2"].current.focusedRowNode();
          switch (colId) {
            case "um":
              const um = Number(rowData.um);
              if (!isNaN(um)) {
                //rowData.um_supply = Math.trunc(um * 1.1);
                focusedRowNode.setDataValue("um_supply", Math.trunc(um * 1.1));
              }
              break;

            case "dt_end":
              const message = dateOk(rowData.dt_start, rowData.dt_end);
              if (!message) {
                showDialog("alert", "종료일이 시작일보다 빠릅니다.");
                return;
              }
              break;
          }
          break;

        case "gridView4":
          focusedRowNode = gridRefs.current["grid4"].current.focusedRowNode();
          switch (colId) {
            case "um":
              const um = Number(rowData.um);
              if (!isNaN(um)) {
                //rowData.um_supply = Math.trunc(um * 1.1);
                focusedRowNode.setDataValue("um_supply", Math.trunc(um * 1.1));
              }
              break;
          }
          break;
      }
    } catch (error: any) {
      console.error("Error in onCellValueChanged:", error.message);
    }
  };
  //#endregion
  const onTabChanged = (index: string) => {
    setSelectedTab(index);
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      if (selectedTab === "1") {
        const cd_cust = selectedRow.cd_cust;
        gridRefs.current["grid2"].current.applyFilters({ cd_cust: cd_cust });
        gridRefs.current["grid2"].current.selectAndFocusRow(0);
      } else {
        const cd_item = selectedRow.cd_item;
        gridRefs.current["grid4"].current.applyFilters({ cd_item: cd_item });
        gridRefs.current["grid4"].current.selectAndFocusRow(0);
      }
    } else {
      return;
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: { onFocusedRowChanged: onFocusedRowChanged },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChanged,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: { onFocusedRowChanged: onFocusedRowChanged },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onBtnAddClick: onGrid4BtnsClick,
          onBtnDeleteClick: onGrid4BtnsClick,
          onBeforeAddRow: onBeforeAddRow2,
          onCellValueChanged: onCellValueChanged,
        },
      },
    }),
    [columns, dataSources]
  );

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1">
        <SnTab
          titles={["거래처별", "폼목별"]}
          width={"100%"}
          onTabChanged={onTabChanged}
          header={[
            <SnTabHeader
              body={
                <div>
                  <SnButton
                    text={"신규품목적용"}
                    onClick={() => {
                      onGrid2BtnsClick("aButton_PopUp");
                    }}
                  ></SnButton>
                </div>
              }
              onBtnAddClick={onGrid2BtnsClick}
              onBtnDeleteClick={onGrid2BtnsClick}
            />,
            <SnTabHeader
              onBtnAddClick={onGrid4BtnsClick}
              onBtnDeleteClick={onGrid4BtnsClick}
            />,
          ]}
          body={[
            <div className="flex w-full h-full min-w-0 space-x-1.5">
              <SnResizeBar direction={"width"} id="panel1" orgSize="29.5%">
                <SnGrid
                  ref={gridRefs.current["grid1"]} // Grid Name
                  columnConfig={grids.grid1.columns} // 컬럼 설정 전달
                  rowData={grids.grid1.dataSource} // Datasource
                  etcProps={{
                    rowSelection: "single",
                    isEditable: false,
                  }} // 기타 설정
                  events={grids.grid1.events}
                  // filter={{
                  //   refs: [gridRefs.current["grid2"]],
                  //   columns: ["cd_cust"],
                  // }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"width"} id="panel2" orgSize="70%">
                <SnGrid
                  ref={gridRefs.current["grid2"]} // Grid Name
                  columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                  rowData={grids.grid2.dataSource} // Datasource
                  etcProps={{
                    rowSelection: "single",
                  }} // 기타 설정
                  events={grids.grid2.events}
                />
              </SnResizeBar>
            </div>,
            <div className="flex w-full h-full min-w-0 space-x-1.5">
              <SnResizeBar direction={"width"} id="panel3" orgSize="29.5%">
                <SnGrid
                  ref={gridRefs.current["grid3"]} // Grid Name
                  columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                  rowData={grids.grid3.dataSource} // Datasource
                  etcProps={{
                    rowSelection: "single",
                    isEditable: false,
                  }} // 기타 설정
                  events={grids.grid3.events}
                  // filter={{
                  //   refs: [gridRefs.current["grid4"]],
                  //   columns: ["cd_item"],
                  // }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"width"} id="panel4" orgSize="70%">
                <SnGrid
                  ref={gridRefs.current["grid4"]} // Grid Name
                  columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                  rowData={grids.grid4.dataSource} // Datasource
                  etcProps={{
                    rowSelection: "single",
                  }} // 기타 설정
                  events={grids.grid4.events}
                />
              </SnResizeBar>
            </div>,
          ]}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_PUR_BAS_REG_003;
